import React from 'react';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { ResponsiveContainer, LineChart, Line } from 'recharts';
import { designTokens } from '../theme/designTokens';

interface StatCardProps {
    title: string;
    value: string;
    icon: React.ReactNode;
    trend?: {
        value: number;
        isPositive: boolean;
        label: string;
    };
    gradient?: string;
    valueColor?: string;
    showValues?: boolean;
    sparklineData?: number[];
    subtitle?: string;
    secondaryTrend?: {
        value: string;
        label: string;
        isPositive: boolean;
    };
    extraLine?: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    icon,
    trend,
    gradient = designTokens.colors.primary.gradient,
    valueColor,
    showValues = true,
    sparklineData,
    subtitle,
    secondaryTrend,
    extraLine,
}) => {
    return (
        <Card
            sx={{
                height: '100%',
                background: gradient,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                transition: `all ${designTokens.transitions.normal}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: designTokens.shadows.xl,
                },
            }}
        >
            {/* Background Icon */}
            <Box
                sx={{
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    opacity: 0.15,
                    fontSize: '120px',
                    zIndex: 0,
                }}
            >
                {icon}
            </Box>

            {/* Sparkline Background */}
            {sparklineData && sparklineData.length > 0 && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '60%',
                        zIndex: 0,
                        opacity: 0.3,
                        pointerEvents: 'none',
                    }}
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={sparklineData.map((val, i) => ({ i, val }))}>
                            <Line
                                type="monotone"
                                dataKey="val"
                                stroke="#fff"
                                strokeWidth={2}
                                dot={false}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </Box>
            )}

            <CardContent sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Title */}
                <Typography
                    variant="body2"
                    sx={{
                        opacity: 0.9,
                        fontWeight: designTokens.typography.fontWeight.medium,
                        marginBottom: 1,
                    }}
                >
                    {title}
                </Typography>

                {/* Value */}
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: designTokens.typography.fontWeight.bold,
                        marginBottom: (trend || subtitle || secondaryTrend) ? 2 : 0,
                        color: valueColor || 'white',
                    }}
                >
                    {showValues ? value : '••••••'}
                </Typography>

                <Box sx={{ mt: 'auto' }}>
                    {/* Secondary Trend (e.g. 24h P&L) */}
                    {secondaryTrend && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                                {secondaryTrend.value}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {secondaryTrend.label}
                            </Typography>
                        </Box>
                    )}

                    {/* Subtitle (e.g. Liquidity Ratio) */}
                    {subtitle && (
                        <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: designTokens.typography.fontWeight.medium, mb: extraLine ? 0.5 : 1 }}>
                            {subtitle}
                        </Typography>
                    )}

                    {extraLine && (
                        <Box sx={{ mb: 1 }}>
                            {extraLine}
                        </Box>
                    )}

                    {/* Trend Indicator */}
                    {trend && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: designTokens.borderRadius.md,
                                padding: '4px 8px',
                                width: 'fit-content',
                            }}
                        >
                            {trend.isPositive ? (
                                <TrendingUp sx={{ fontSize: 16, marginRight: 0.5 }} />
                            ) : (
                                <TrendingDown sx={{ fontSize: 16, marginRight: 0.5 }} />
                            )}
                            <Typography variant="caption" sx={{ fontWeight: designTokens.typography.fontWeight.semibold }}>
                                {trend.value > 0 ? '+' : ''}{trend.value.toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" sx={{ marginLeft: 0.5, opacity: 0.8 }}>
                                {trend.label}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default StatCard;
